import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Users, Compass, TrendingUp, HeartHandshake } from "lucide-react";
import { HeroVideo } from "@/components/HeroVideo";
import { UnitCard } from "@/components/UnitCard";
import { Reviews } from "@/components/Reviews";
import { ActionLink } from "@/components/ActionLink";
import { RotatingPhrases } from "@/components/RotatingPhrases";
import { UNITS } from "@/data/units";
import { openTrialDialog } from "@/components/TrialDialog";
import { track } from "@/lib/tracking";
import { DAY_USE, formatBRL } from "@/data/plans";
import { ImageCarousel } from "@/components/ImageCarousel";
import { LazyVideo } from "@/components/LazyVideo";
import heroPoster from "@/assets/hero-poster.jpg.asset.json";
import metodoVideo from "@/assets/metodo-assistido.mp4.asset.json";
import comunidadeVideo from "@/assets/comunidade-nook.mp4.asset.json";
import carousel01 from "@/assets/assistido/assistido-1.jpg.asset.json";
import carousel02 from "@/assets/assistido/assistido-2.jpg.asset.json";
import carousel03 from "@/assets/assistido/assistido-3.jpg.asset.json";
import carousel04 from "@/assets/assistido/assistido-4.jpg.asset.json";
import carousel05 from "@/assets/assistido/assistido-5.jpg.asset.json";

const TITLE = "Nook Gym | Academia com treino personalizado no Rio";
const DESCRIPTION =
  "Treino personalizado com até três alunos por instrutor. Conheça as unidades Nook no Recreio, Barra da Tijuca e Avenida das Américas.";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://nookgym.com.br/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow" },
    ],
    links: [
      { rel: "canonical", href: "https://nookgym.com.br/" },
      { rel: "preload", as: "image", href: heroPoster.url, fetchpriority: "high" },
    ],
  }),
  component: Home,
});

const PILLARS = [
  {
    icon: Users,
    title: "Até três alunos por instrutor",
    text: "Acompanhamento próximo, com atenção à execução e ao ritmo de cada aluno.",
  },
  {
    icon: HeartHandshake,
    title: "Treino preparado para você",
    text: "Cada sessão é planejada a partir dos seus objetivos e da sua evolução.",
  },
  {
    icon: Compass,
    title: "Acompanhamento na execução",
    text: "Orientação técnica, correções e ajustes ao longo de todo o treino.",
  },
  {
    icon: TrendingUp,
    title: "Evolução com continuidade",
    text: "Seu histórico é registrado para orientar a próxima sessão e o seu progresso.",
  },
];

const CAROUSEL_IMAGES = [
  {
    src: carousel01.url,
    alt: "Instrutora Nook acompanhando aluno em desenvolvimento de ombros com halteres",
  },
  {
    src: carousel02.url,
    alt: "Instrutor Nook assistindo aluno no supino com halteres",
  },
  {
    src: carousel03.url,
    alt: "Instrutora Nook orientando aluno durante série com halteres",
  },
  {
    src: carousel04.url,
    alt: "Instrutor Nook corrigindo a execução de rosca direta de uma aluna",
  },
  {
    src: carousel05.url,
    alt: "Instrutor Nook assistindo aluna em elevação frontal com halter",
  },
];

function Home() {
  return (
    <>
      {/* Hero */}
      <section className="relative flex min-h-[92svh] items-end overflow-hidden">
        <HeroVideo />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-20 pt-32 lg:px-8 lg:pb-28">
          <div className="max-w-3xl animate-rise">
            <span className="eyebrow">Nookers Performance Studio</span>
            <h1 className="display mt-6 text-[3rem] leading-[0.92] sm:text-[4.5rem] lg:text-[5.75rem]">
              Treinamento acompanhado
              <br />
              <span className="text-nook-green">de verdade.</span>
            </h1>
            <p className="mt-7 max-w-xl text-base text-muted-foreground sm:text-lg">
              Até três alunos por instrutor.
              <br />
              Presença, correção e cuidado durante todo o treino.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <ActionLink to="/metodo-nook" variant="outline">
                Conheça o método
              </ActionLink>
              <ActionLink
                variant="green"
                onClick={() => {
                  track("click_experimental", { where: "hero" });
                  openTrialDialog("hero");
                }}
              >
                Garanta seu Day Use →
              </ActionLink>
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.16em] text-muted-foreground">
              Conheça nosso acompanhamento na prática.
            </p>
          </div>
        </div>
      </section>

      {/* Diferencial */}
      <section className="border-t border-border py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="eyebrow">O diferencial Nook</span>
              <h2 className="display mt-5 text-4xl sm:text-5xl">
                Aqui, ninguém
                <br />
                treina sozinho.
              </h2>
              <div className="hairline mt-8 w-32" />
              <p className="mt-8 text-sm leading-relaxed text-muted-foreground">
                A Nook não é uma academia onde você entra, se vira e vai embora. É musculação com
                acompanhamento: um instrutor presente do aquecimento à última série.
              </p>
              <div className="mt-10">
                <ImageCarousel images={CAROUSEL_IMAGES} />
              </div>
            </div>

            <div className="flex h-full lg:col-span-7">
              <ul className="grid h-full w-full grid-cols-1 gap-px overflow-hidden rounded-sm border border-border bg-border sm:grid-cols-2 sm:grid-rows-2">
                {PILLARS.map((p) => (
                  <li key={p.title} className="flex flex-col justify-between bg-background p-6 sm:h-full sm:p-8">
                    <div>
                      <p.icon className="h-5 w-5 text-nook-green" aria-hidden="true" />
                      <h3 className="mt-4 text-lg font-semibold leading-tight text-foreground sm:mt-6">{p.title}</h3>
                    </div>
                    <p className="mt-3 text-sm leading-snug text-muted-foreground">{p.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Método */}
      <section className="border-t border-border bg-card py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-start gap-8 lg:grid-cols-12 lg:grid-rows-[auto_1fr] lg:gap-x-14 lg:gap-y-10">
            <div className="order-1 lg:order-2 lg:col-span-7 lg:col-start-6 xl:col-span-8 xl:col-start-5">
              <span className="eyebrow">Método</span>
              <h2 className="display mt-6 text-[2.75rem] leading-[0.92] sm:text-5xl lg:text-[4.5rem] lg:leading-[0.92] xl:text-[5.25rem]">
                Cuidado
                <br />
                é o nosso método.
              </h2>
            </div>

            <div className="order-2 lg:order-1 lg:col-span-5 lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:self-center xl:col-span-4">
              <LazyVideo
                src={metodoVideo.url}
                label="Alunos da Nook treinando com acompanhamento do instrutor"
                className="aspect-[9/16] w-full max-w-[280px] overflow-hidden rounded-sm lg:max-w-none"
              />
            </div>

            <div className="order-3 lg:col-span-7 lg:col-start-6 lg:row-start-2 lg:self-start xl:col-span-8 xl:col-start-5">
              <p className="max-w-2xl text-lg leading-relaxed text-foreground lg:text-xl">
                Cada treino tem direção. Seu programa é preparado, acompanhado e ajustado conforme a
                sua evolução.
              </p>

              <div className="mt-8 grid gap-6 border-t border-border pt-8 sm:grid-cols-2 sm:gap-10">
                <p className="text-[0.95rem] leading-relaxed text-muted-foreground">
                  No máximo três alunos por sessão, cada um seguindo o próprio treino. O resultado é
                  o que a musculação sozinha raramente dá: segurança, constância e qualidade técnica
                  que se acumula.
                </p>
                <p className="hidden text-[0.95rem] leading-relaxed text-muted-foreground lg:block">
                  O acompanhamento não termina com o treino: o que você faz hoje fica registrado e
                  orienta como vamos continuar na próxima sessão.
                </p>
              </div>


              <div className="mt-10">
                <Link
                  to="/metodo-nook"
                  className="group inline-flex items-center gap-2 border-b border-nook-green pb-2 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-foreground transition-colors hover:text-nook-green"
                >
                  Entenda o método Nook
                  <ArrowRight className="h-4 w-4 text-nook-green transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Unidades */}
      <section id="unidades" className="scroll-mt-20 border-t border-border py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <span className="eyebrow">Unidades e planos</span>
          <h2 className="display mt-5 text-4xl sm:text-5xl">Onde a Nook acontece.</h2>
          <p className="mt-4 max-w-xl text-sm text-muted-foreground">
            Três unidades, o mesmo método. Consulte os planos de cada uma.
          </p>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {UNITS.map((u) => (
              <UnitCard key={u.slug} unit={u} />
            ))}
          </div>
        </div>
      </section>

      {/* Comunidade */}
      <section id="comunidade" className="border-t border-border bg-card py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-start gap-8 px-5 lg:grid-cols-12 lg:gap-x-14 lg:gap-y-8 lg:px-8">
          <div className="order-1 lg:order-2 lg:col-span-7 lg:col-start-6 xl:col-span-8 xl:col-start-5">
            <span className="eyebrow">Comunidade</span>
            <h2 className="display mt-6 text-[2.75rem] leading-[0.92] sm:text-5xl lg:text-[4.5rem] lg:leading-[0.92]">
              Gente real.
              <br />
              Treino bem feito.
            </h2>
          </div>
          <div className="order-2 lg:order-1 lg:col-span-5 lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:self-center xl:col-span-4">
            <LazyVideo
              src={comunidadeVideo.url}
              label="Alunos da Nook treinando juntos na comunidade"
              className="aspect-[9/16] w-full max-w-[280px] overflow-hidden rounded-sm lg:max-w-none"
            />
          </div>
          <div className="order-3 lg:col-span-7 lg:col-start-6 lg:row-start-2 xl:col-span-8 xl:col-start-5">
            <RotatingPhrases
              label="Quem são os Nookers"
              interval={2800}
              phrases={[
                "Quem está começando do zero",
                "Quem voltou depois de anos parado",
                "Quem treina há tempo e quer performance",
                "Quem precisa de rotina e constância",
              ]}
            />
          </div>
        </div>
      </section>


      {/* Avaliações */}
      <section className="border-t border-border py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <span className="eyebrow">Avaliações</span>
          <h2 className="display mt-5 max-w-2xl text-4xl sm:text-5xl">
            A experiência de quem{" "}
            <br className="hidden sm:block" />
            treina com a gente.
          </h2>
          <p className="mt-4 max-w-2xl text-sm text-muted-foreground">
            Todas nossas unidades são 5 estrelas no Google.
          </p>
          <div className="mt-10">
            <Reviews />
          </div>
        </div>
      </section>

      {/* CTA final — Day Use Nook */}
      <section className="relative overflow-hidden border-t border-border bg-offwhite py-24 text-offwhite-foreground">
        <img
          src={carousel01.url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-multiply"
        />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:items-end lg:px-8">
          <div className="lg:col-span-7">
            <span className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-offwhite-foreground/70">
              Próximo passo
            </span>
            <h2 className="display mt-5 max-w-2xl text-4xl sm:text-5xl">
              Viva o método
              <br />
              na prática.
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-offwhite-foreground/70">
              Uma experiência para conhecer o acompanhamento que faz parte de cada treino.
            </p>
          </div>
          <div className="border-t border-offwhite-foreground/20 pt-8 lg:col-span-5 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
            <p className="text-[0.72rem] font-semibold uppercase tracking-[0.18em]">{DAY_USE.name}</p>
            <p className="display mt-3 text-6xl">{formatBRL(DAY_USE.price).replace(",00", "")}</p>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-offwhite-foreground/70">{DAY_USE.credit}</p>
            <button
              type="button"
              onClick={() => {
                track("click_experimental", { where: "cta_final" });
                track("institutional_day_use_click", { source_section: "home_day_use" });
                openTrialDialog("cta_final");
              }}
              className="mt-8 inline-flex min-h-13 items-center rounded-sm bg-ink px-7 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-foreground transition-transform hover:-translate-y-[1px]"
            >
              Garanta seu Day Use →
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
